import { useMemo } from "react";

const T = "\uFE0E"; // forza la resa testuale (niente emoji colorate)
const SIGN_GLYPHS = ["♈", "♉", "♊", "♋", "♌", "♍", "♎", "♏", "♐", "♑", "♒", "♓"];
const SYM_FONT = "'Segoe UI Symbol','Noto Sans Symbols 2','Noto Sans Symbols','DejaVu Sans','Apple Symbols',serif";
const ASPECTS = [
  { angle: 0, orb: 7, color: "#B9863F", name: "congiunzione" },
  { angle: 60, orb: 5, color: "#B9863F", name: "sestile" },
  { angle: 90, orb: 6, color: "#8A5A3C", name: "quadratura" },
  { angle: 120, orb: 6, color: "#D4A056", name: "trigono" },
  { angle: 180, orb: 7, color: "#8A5A3C", name: "opposizione" },
];

export default function NatalWheel({ chart, size = 420 }) {
  const cx = size / 2, cy = size / 2;
  const R = size / 2 - 22;           // outer
  const rSign = R - 30;              // inner edge of sign ring
  const rPlanet = rSign - 34;        // planet orbit
  const rInner = rPlanet - 34;       // aspect circle
  const asc = chart.angles?.Ascendente?.longitude ?? 0;

  const toXY = (lon, r) => {
    const th = ((180 + (lon - asc)) * Math.PI) / 180;
    return [cx + r * Math.cos(th), cy - r * Math.sin(th)];
  };

  const planets = useMemo(() => {
    const sorted = [...chart.planets].sort((a, b) => a.longitude - b.longitude);
    let prev = null, flip = false;
    return sorted.map((p) => {
      const close = prev !== null && Math.abs(p.longitude - prev) < 7;
      flip = close ? !flip : false;
      prev = p.longitude;
      return { ...p, r: rPlanet + (flip ? -20 : 0) };
    });
  }, [chart.planets, rPlanet]);

  const aspects = useMemo(() => {
    const body = chart.planets.filter((p) => p.name !== "Nodo Nord");
    const out = [];
    for (let i = 0; i < body.length; i++) for (let j = i + 1; j < body.length; j++) {
      let d = Math.abs(body[i].longitude - body[j].longitude); if (d > 180) d = 360 - d;
      const a = ASPECTS.find((x) => Math.abs(d - x.angle) <= x.orb);
      if (a && a.angle !== 0) out.push({ a: body[i], b: body[j], asp: a });
    }
    return out;
  }, [chart.planets]);

  return (
    <svg viewBox={`0 0 ${size} ${size}`} className="w-full h-auto" data-testid="natal-wheel" role="img" aria-label="Ruota del tema natale" style={{ fontFamily: SYM_FONT }}>
      <defs>
        <radialGradient id="wheelBg" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#FAF3E4" />
          <stop offset="100%" stopColor="#EFE3CE" />
        </radialGradient>
      </defs>
      <circle cx={cx} cy={cy} r={R} fill="url(#wheelBg)" stroke="#B9863F" strokeWidth="1.2" />
      <circle cx={cx} cy={cy} r={rSign} fill="none" stroke="#B9863F" strokeWidth="0.8" />
      <circle cx={cx} cy={cy} r={rInner} fill="#F7EFE0" stroke="#2A1F14" strokeOpacity="0.15" strokeWidth="0.8" />

      {/* Sign segments */}
      {SIGN_GLYPHS.map((g, i) => {
        const [x1, y1] = toXY(i * 30, R); const [x2, y2] = toXY(i * 30, rSign);
        const [gx, gy] = toXY(i * 30 + 15, (R + rSign) / 2);
        return (
          <g key={i}>
            <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="#B9863F" strokeOpacity="0.6" strokeWidth="0.8" />
            <text x={gx} y={gy} textAnchor="middle" dominantBaseline="central" fontSize="15" fill="#8A5A3C">{g + T}</text>
          </g>
        );
      })}

      {/* Degree ticks */}
      {Array.from({ length: 72 }).map((_, i) => {
        const [x1, y1] = toXY(i * 5, rSign); const [x2, y2] = toXY(i * 5, rSign - (i % 6 === 0 ? 8 : 4));
        return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#2A1F14" strokeOpacity="0.25" strokeWidth="0.7" />;
      })}

      {/* Houses */}
      {chart.houses && chart.houses.map((c, i) => {
        const [x1, y1] = toXY(c, rSign - 8); const [x2, y2] = toXY(c, rInner);
        const [nx, ny] = toXY(c + ((chart.houses[(i + 1) % 12] - c + 360) % 360) / 2, rInner + 12);
        const strong = i % 3 === 0;
        return (
          <g key={i}>
            <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="#2A1F14" strokeOpacity={strong ? 0.6 : 0.2} strokeWidth={strong ? 1.3 : 0.7} />
            <text x={nx} y={ny} textAnchor="middle" dominantBaseline="central" fontSize="9" fill="#2A1F14" fillOpacity="0.45">{i + 1}</text>
          </g>
        );
      })}

      {/* Aspects */}
      {aspects.map(({ a, b, asp }, i) => {
        const [x1, y1] = toXY(a.longitude, rInner); const [x2, y2] = toXY(b.longitude, rInner);
        return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke={asp.color} strokeOpacity="0.55" strokeWidth="0.9" />;
      })}

      {/* Planets */}
      {planets.map((p) => {
        const [x, y] = toXY(p.longitude, p.r);
        const [tx, ty] = toXY(p.longitude, rInner + 3);
        return (
          <g key={p.name}>
            <line x1={tx} y1={ty} x2={x} y2={y} stroke="#B9863F" strokeOpacity="0.35" strokeWidth="0.6" />
            <circle cx={x} cy={y} r="11" fill="#F7EFE0" stroke="#B9863F" strokeWidth="0.8" />
            <text x={x} y={y + 0.5} textAnchor="middle" dominantBaseline="central" fontSize="13" fill="#2A1F14">{p.symbol + T}</text>
          </g>
        );
      })}

      {/* Angles */}
      {chart.angles?.Ascendente && (() => {
        const [ax, ay] = toXY(asc, R + 12);
        const [mx, my] = toXY(chart.angles["Medio Cielo"].longitude, R + 12);
        return (
          <>
            <text x={ax} y={ay} textAnchor="middle" dominantBaseline="central" fontSize="10" fill="#B9863F" fontWeight="600">AC</text>
            <text x={mx} y={my} textAnchor="middle" dominantBaseline="central" fontSize="10" fill="#B9863F" fontWeight="600">MC</text>
          </>
        );
      })()}
    </svg>
  );
}
